import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import tooltip from '@/mixins/components/tooltip';

export default Vue.component('input-slider', {
  template: require('./template.html'),
  name: 'input-slider',
  components: {
    tooltip,
  },
  computed: {
    ...mapGetters(['viewport', 'activeDroplet', 'activeDropletItem']),
    allColumns() {
      let list = [];
      const droplet = this.activeDroplet;
      if (droplet.rows) {
        _.forEach(this.activeDroplet.rows,
          row => _.forEach(row.columns, column => list.push(column)));
      }
      if (droplet.cards) {
        list = list.concat(droplet.cards);
      }
      if (droplet.columns) {
        list = list.concat(droplet.columns);
      }
      return { list };
    },
    hasAreas() {
      return Object.keys(this.areas).length > 0;
    },
    areaChecked() {
      return this.hasAreas && !this.areas.all && Object.values(this.areas).includes(true);
    },
  },
  props: [
    'option',
    'active-state',
    'dom-nodes',
    'component-config',
  ],
  data() {
    return {
      display: {},
      debTime: 250,
      innerDomNodes: {},
      areas: {},
    };
  },
  watch: {
    viewport() {
      this.setInitialValue();
    },
    activeState() {
      this.setInitialValue();
    },
    activeDropletItem: {
      handler() {
        this.setInitialValue();
      },
      deep: true,
    },
  },
  created() {
    // setup defaults from payload models
    _.forOwn(this.option.payload.models, (model) => {
      this.$set(this.display, model.name, model.default);
    });
    if (this.option.payload.areas) {
      _.forOwn(this.option.payload.areas, (area) => {
        this.$set(this.areas, area.property, area.property === 'all');
      });
    }
    // then check for previous values
    this.setInitialValue();
  },
  methods: {
    setInitialValue() {
      const option = this.option;
      let values;
      const payload = {
        $vm: {
          viewport: this.viewport,
          currentVal: null,
          droplet: this.activeDroplet,
        },
      };
      if (option.$el) {
        const useRoot = option.$el === 'root';
        const $root = this.domNodes.$dropletRow;
        payload.$el = useRoot ? $root : $root.querySelectorAll(option.$el)[0];
      }
      let rule = option.payload.updates[0];
      // if one area is checked, get that rule from the updates list
      if (this.areaChecked) {
        const checked = _.keys(_.pickBy(this.areas));
        rule = _.find(option.payload.updates, update => checked.includes(update.property));
      }
      let selector;
      if (this.activeDropletItem._id && this.componentConfig.itemList) {
        const inner = this.componentConfig.itemList.selectors[rule.property];
        const nested = inner || '';
        selector = `#drp-${this.activeDropletItem._id}${nested}${this.activeState}`;
      } else {
        selector = rule.selector.replace(/\$\{state\}/gi, this.activeState);
        selector = selector.replace(/\$\{dropletId\}/gi, this.activeDroplet._id);
      }
      payload.selector = selector;
      payload.property = rule.property;
      payload.defaultVal = option.payload.models.value.default;
      if (payload.property === 'transform') {
        payload.type = option.payload.type;
        values = this._getValTransform(payload);
      } else {
        payload.defaultUnit = option.payload.models.unit.default;
        values = this._getVal(payload);
      }
      this.display[option.payload.models.value.name] = values.val;
      this.display[option.payload.models.unit.name] = this.getUnit({ unit: values.unit, action: 'get' });
    },
    changeCSS(params) {
      const payload = {};
      payload.drp = this.activeDroplet;
      payload.val = this.buildVal(params);
      payload.viewport = this.viewport;
      let updates = params.updates;
      // if there is at least one area checked besides all
      if (this.hasAreas && this.areaChecked) {
        const checked = _.keys(_.pickBy(this.areas));
        updates = _.filter(updates, update => checked.includes(update.property));
      }
      // we only want to show updates to the dom if in default state
      if (['', ':reveal'].includes(this.activeState) && !this.activeDropletItem._id) {
        const properties = updates.map(item => item.local || item.property);
        this.setLocals({ ...payload, properties });
      }
      // slide inputs will always need a debounce
      return new Promise((resolve) => {
        this._debounce(() => {
          _.forEach(updates, (item) => {
            let selector;
            let special;
            if (this.activeDropletItem._id && this.componentConfig.itemList) {
              // this is in the event a user is targeting a specific item within a droplet
              // eg, a nav link or a column in columns
              let nested = '';
              const itemProperties = this.componentConfig.itemList.properties;
              if (itemProperties) {
                nested = itemProperties[item.selector].selector || '';
              } else {
                nested = this.componentConfig.itemList.selectors[item.property] || '';
              }
              const defaultSel = `#drp-${this.activeDropletItem._id}${nested}`;
              selector = `${defaultSel}${this.activeState}`;
              if (item.special) {
                special = defaultSel;
              }
            } else {
              this.resetCustomQueries(item);
              selector = item.selector.replace(/\$\{state\}/gi, this.activeState);
              selector = selector.replace(/\$\{dropletId\}/gi, this.activeDroplet._id);
            }
            payload.property = item.property;
            payload.noId = item.noId;
            payload.selector = selector;
            if (item.valueHelper) {
              payload.val = this[item.valueHelper](payload.val);
            }
            if (item.callback) {
              this[item.callback](payload);
            }
            if (item.value && payload.val !== 'default') {
              payload.val = item.value;
            }
            this.$emit('check-legacy-styles', payload);
            this.$store.dispatch('changeDropletCss', payload);
            if (special) {
              _.forOwn(item.special, (value, key) => {
                this.$store.dispatch('changeDropletCss', {
                  ...payload,
                  selector: `${special}${key}${this.activeState}`,
                  val: value,
                });
              });
            }
          });
          this.$emit('css-generator', this.cssPayload(), this.option.plugin);
          this.$emit('update-changes', true);
          this.setLocals({ val: null, properties: [] });
          resolve();
        }, params.debTime || this.debTime);
      });
    },
    resetCustomQueries(update) {
      if (!this.componentConfig.itemList) {
        return;
      }
      const inner = this.componentConfig.itemList.selectors[update.property];
      const nested = inner || '';
      const customRule = new RegExp(`#drp-[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}${nested}`, 'gi');
      _.forEach(this.activeDroplet.mediaQueries, (rule) => {
        if (rule.selector.match(customRule)) {
          const property = _.find(rule.properties, r => r.property === update.property);
          if (property) {
            this.$store.dispatch('setDropletVal', {
              drp: property,
              key: this.viewport,
              val: 'default',
            });
          }
        }
      });
    },
    checkArea(area) {
      _.forOwn(this.areas, (value, key) => {
        if (area !== key) {
          this.areas[key] = false;
        }
      });
      if ((area === 'all' && !this.areas.all) || (area !== 'all' && !this.areaChecked)) {
        this.areas.all = true;
      }
      this.setInitialValue();
    },
    getUnit(params) {
      const converts = {
        get: {
          s: 'seconds',
        },
        build: {
          seconds: 's',
        },
      };
      return converts[params.action][params.unit] || params.unit;
    },
    async eraseProperty(params) {
      await this.changeCSS({
        ...params,
        erase: true,
        debTime: 1,
      });
      this.setInitialValue();
    },
    buildVal(params) {
      if (params.erase) {
        return 'default';
      }
      const first = params.updates[0];
      const property = first.property;
      const value = this.display[params.models.value.name];
      const unit = this.getUnit({ unit: this.display[params.models.unit.name], action: 'build' });
      let v = `${value}${unit}`;
      if (property === 'transform') {
        const selector = first.selector.replace(/\$\{state\}/gi, this.activeState);
        const payload = {
          $vm: {
            viewport: this.viewport,
            currentVal: null,
            droplet: this.activeDroplet,
          },
          selector,
          property,
          type: params.type,
          el: { value },
          unit,
        };
        v = this._buildTransformVal(payload);
      }
      return v;
    },
    setLocals(params) {
      this.$store.dispatch('setLocalStyles', params);
    },
    cssPayload() {
      let payload = false;
      if (this.option.payload.useColumns) {
        payload = {
          key: 'drps',
          value: [...this.allColumns.list, this.activeDroplet],
        };
      }
      return payload;
    },
    socialSizer(val) {
      const num = parseInt(val.split('px')[0], 10);
      return `${(num * 0.65).toFixed(2)}px`;
    },
    headerText(val) {
      let newText = this.activeDroplet.text;
      if (newText.match(/font-size/gi)) {
        newText = newText.replace(/font-size:(.*?);/, '');
        this.$store.dispatch('setHeaderText', {
          drp: this.activeDroplet,
          text: newText,
        });
      }
      return val;
    },
    headerHint() {
      const size = this.display.headerSize;
      let tags = 'h1';
      if (size <= 14) {
        tags = 'h6';
      }
      if (size >= 15 && size <= 19) {
        tags = 'h5';
      }
      if (size >= 20 && size <= 24) {
        tags = 'h4';
      }
      if (size >= 25 && size <= 29) {
        tags = 'h3';
      }
      if (size >= 30 && size <= 34) {
        tags = 'h2';
      }
      return tags;
    },
    buildRevealOptions(params) {
      const defaults = {
        'transition-delay': {
          desktop: 'default',
          tablet: 'default',
          mobile: 'default',
        },
        transition: {
          desktop: 'default',
          tablet: 'default',
          mobile: 'default',
        },
      };
      const drp = this.activeDroplet;
      const revealOptions = drp.revealOptions ? _.cloneDeep(drp.revealOptions) : defaults;
      revealOptions[params.property][this.viewport] = params.val;
      this.$store.dispatch('setDropletVal', {
        drp,
        key: 'revealOptions',
        val: revealOptions,
      });
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/style-sidebar/options/input-slider/index.js